import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/utils";

interface OrderItemModifier {
  name: string;
  price: number;
}

interface OrderLineItem {
  name: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  modifiers: OrderItemModifier[];
  notes?: string;
}

interface OrderItemRowProps {
  item: OrderLineItem;
  index: number;
  onUpdateQuantity: (index: number, qty: number) => void;
  onRemoveItem: (index: number) => void;
}

export function OrderItemRow({ item, index, onUpdateQuantity, onRemoveItem }: OrderItemRowProps) {
  return (
    <div className="group py-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-base font-medium text-white truncate">{item.name}</p>

          {item.modifiers.length > 0 && (
            <ul className="mt-1 space-y-0.5">
              {item.modifiers.map((mod, i) => (
                <li key={i} className="pl-3 text-xs text-text-dim">
                  {mod.name}
                  {mod.price > 0 && (
                    <span className="ml-1 text-text-dim">+{formatCurrency(mod.price)}</span>
                  )}
                </li>
              ))}
            </ul>
          )}

          {item.notes && (
            <p className="mt-1 pl-3 text-xs italic text-text-dim">{item.notes}</p>
          )}
        </div>

        <div className="text-right shrink-0">
          <p className="text-base font-semibold text-white">{formatCurrency(item.totalPrice)}</p>
        </div>

        <button
          onClick={() => onRemoveItem(index)}
          className={cn(
            "shrink-0 rounded-xl p-2 text-text-dim transition-all touch-target",
            "hover:bg-danger/10 hover:text-danger",
            "opacity-0 group-hover:opacity-100",
          )}
          aria-label={`Remove ${item.name}`}
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="mt-2 flex items-center gap-2">
        <div className="inline-flex items-center rounded-xl border border-surface-border bg-surface">
          <button
            onClick={() => onUpdateQuantity(index, item.quantity - 1)}
            disabled={item.quantity <= 1}
            className={cn(
              "flex h-9 w-9 items-center justify-center rounded-l-xl text-text-muted transition-colors",
              "hover:bg-white/10 hover:text-white",
              "disabled:opacity-40 disabled:cursor-not-allowed",
            )}
            aria-label="Decrease quantity"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4" />
            </svg>
          </button>
          <span className="flex h-9 w-10 items-center justify-center text-sm font-semibold text-white">
            {item.quantity}
          </span>
          <button
            onClick={() => onUpdateQuantity(index, item.quantity + 1)}
            className={cn(
              "flex h-9 w-9 items-center justify-center rounded-r-xl text-text-muted transition-colors",
              "hover:bg-white/10 hover:text-white",
            )}
            aria-label="Increase quantity"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>
        <span className="text-xs text-text-dim">
          @ {formatCurrency(item.unitPrice)} ea
        </span>
      </div>
    </div>
  );
}
